import {
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import ArrowBackIosOutlinedIcon from '@material-ui/icons/ArrowBackIosOutlined';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import locale from '../locale/Localization';
import NotaryContracts from './NotaryContracts';
import NotaryFunctions from './NotaryFunctions';

const styles = {
  root: {width: '100%'},
  heading: {marginBottom: '10px'},
  title: {paddingTop: '20px', paddingBottom: '20px', color: 'white'},
  tableheader: {fontWeight: 'bold'},
  tableCellTh: {width: '50%'},
  list: {padding: 0},
  listitem: {paddingTop: 0, paddingBottom: 0, paddingLeft: 0},
  button: {marginTop: '20px', color: 'white'}
};

/**
 * Component class of NotaryDetails.
 */
export default class NotaryDetails extends Component {
  // eslint-disable-next-line require-jsdoc
  constructor(props) {
    super(props);
    this.state = {
      notaryDetails: props.notaryDetails,
      function: props.notaryDetails.functions
    };
  }

  /**
   * Renders the element on the details page.
   *
   * @param {string} label - The element's label.
   * @param {string} value - The element's value.
   * @return {string} - The element to be rendered.
   */
  renderDetailsComponent = (label: string, value: string) => {
    return (
      <TableRow>
        <TableCell component='th' scope='row' style={styles.tableCellTh}>
          {label}
        </TableCell>
        {value && <TableCell>{value}</TableCell>}
        {value == null && <TableCell></TableCell>}
      </TableRow>
    );
  };

  /**
   * Renders notary's public information on notary's details page.
   *
   * @return {string} - The public information to be rendered.
   */
  renderNotaryPublicInfo = () => {
    return (
      <div id='notary-public-info' style={styles.heading}>
        <TableContainer component={Paper}>
          <Table aria-label='public-info-table'>
            <TableHead>
              <TableRow>
                <TableCell style={styles.tableheader}>
                  {locale.notaryPublicInfo}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {this.renderDetailsComponent(
                locale.name,
                this.state.notaryDetails.officialName
              )}
              {this.renderDetailsComponent(
                locale.firstName,
                this.state.notaryDetails.firstNames
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  /**
   * Renders notary's organisation context section on notary's details page.
   *
   * @return {string} - The organisation context to be rendered.
   */
  renderNotaryOrganisationContext = () => {
    return (
      <div id='notary-organisation-context' style={styles.heading}>
        <TableContainer component={Paper}>
          <Table aria-label='organisation-info-table'>
            <TableHead>
              <TableRow>
                <TableCell style={styles.tableheader}>
                  {locale.notaryOrganisationContext}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {this.renderDetailsComponent(
                locale.organisation,
                this.state.notaryDetails.organisation.name
              )}
              {this.renderDetailsComponent(
                locale.uidNumber,
                this.state.notaryDetails.organisation.uid
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  /**
   * Renders notary's company address on notary's details page.
   *
   * @return {string} - The company address to be rendered.
   */
  renderCompanyAddress = () => {
    return (
      <TableRow>
        <TableCell component='th' scope='row' style={styles.tableCellTh}>
          {locale.companyAddress}
        </TableCell>
        <TableCell>
          <List dense style={styles.list}>
            <ListItem style={styles.listitem}>
              <ListItemText
                primary={this.state.notaryDetails.organisation.name}
              />
            </ListItem>
            <ListItem style={styles.listitem}>
              <ListItemText
                primary={
                  <span>
                    {
                      this.state.notaryDetails.organisation.contactInformation
                        .street
                    }
                    &nbsp;
                    {
                      this.state.notaryDetails.organisation.contactInformation
                        .houseNumber
                    }
                  </span>
                }
              />
            </ListItem>
            <ListItem style={styles.listitem}>
              <ListItemText
                primary={
                  <span>
                    {
                      this.state.notaryDetails.organisation.contactInformation
                        .swissZip
                    }
                    &nbsp;
                    {
                      this.state.notaryDetails.organisation.contactInformation
                        .town
                    }
                  </span>
                }
              />
            </ListItem>
          </List>
        </TableCell>
      </TableRow>
    );
  };

  /**
   * Renders notary's contact info on notary's details page.
   *
   * @return {string} - The contact info to be rendered.
   */
  renderNotaryContactInfo = () => {
    return (
      <div id='notary-contact-info' style={styles.heading}>
        <TableContainer component={Paper}>
          <Table aria-label='contact-info-table'>
            <TableHead>
              <TableRow>
                <TableCell style={styles.tableheader}>
                  {locale.notaryContactInfo}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {this.renderCompanyAddress()}
              {this.renderDetailsComponent(
                locale.phoneNumber,
                this.state.notaryDetails.organisation.contactInformation
                  .telephone
              )}
              {this.renderDetailsComponent(
                locale.faxNumber,
                this.state.notaryDetails.organisation.contactInformation.fax
              )}
              {this.renderDetailsComponent(
                locale.emailAddresse,
                this.state.notaryDetails.organisation.contactInformation.mail
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  /**
   * Renders notary's details page.
   *
   * @return {string} - The details page to be rendered.
   */
  renderDetails = () => {
    return (
      <div id='notary-details-container' style={styles.root}>
        {this.renderNotaryPublicInfo()}
        {this.renderNotaryOrganisationContext()}
        {this.renderNotaryContactInfo()}
      </div>
    );
  };

  /**
   * Renders NotairyDetils component.
   *
   * @return {string} - The notary details component to be rendered.
   */
  render() {
    return (
      <div>
        <Button
          style={styles.button}
          size='small'
          onClick={() => {
            this.props.onBackLinkClick();
          }}
          startIcon={<ArrowBackIosOutlinedIcon />}
        >
          {locale.back}
        </Button>
        <Typography variant='h5' gutterBottom style={styles.title}>
          {locale.notaryDetailsPageTitle}
        </Typography>
        {this.state.notaryDetails && this.renderDetails()}
        <NotaryFunctions
          personId={this.props.notaryDetails.id}
          certificate={this.props.notaryDetails.certificate}
          functions={this.props.notaryDetails.functions}
          viewMode={this.props.viewMode}
        />
        <NotaryContracts
          personId={this.props.notaryDetails.id}
          signatureDeclaration={
            this.props.notaryDetails.signatureDeclarationDocument
          }
          termination={this.props.notaryDetails.terminationDocument}
          registration={this.props.notaryDetails.registrationDocument}
          editMode={false}
        />
      </div>
    );
  }
}

NotaryDetails.propTypes = {
  notaryDetails: PropTypes.object.isRequired,
  onBackLinkClick: PropTypes.func.isRequired,
  viewMode: PropTypes.string.isRequired
};
